import {
  CartesiaVoicePersona,
  ElevenLabsVoicePersona,
  OpenAiRealtimeVoicePersona,
  OpenAiStandardVoicePersona,
  VoiceCallFirstMessageMode,
  VoiceCallLanguage,
  type VoicePersona,
} from 'flow-schema';
import { z } from 'zod';

import { api } from 'common';

const { Gender } = api.Common;

export const VoicePersonaDescriptionSchema = z.object({
  description: z.string(),
  gender: z.enum(Gender).optional(),
  languages: z.array(z.enum(VoiceCallLanguage)).optional(),
  persona: z.string().optional(),
});

type VoicePersonaDescription = z.infer<typeof VoicePersonaDescriptionSchema>;

export const VoicePersonaDescriptions: Record<VoicePersona, VoicePersonaDescription> = {
  [ElevenLabsVoicePersona.Alice]: {
    persona: 'Alice',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English],
    description: 'Female British',
  },
  [ElevenLabsVoicePersona.Arabella]: {
    persona: 'Arabella',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English],
    description: 'Female American',
  },
  [ElevenLabsVoicePersona.Arnold]: {
    persona: 'Arnold',
    gender: Gender.Male,
    languages: [VoiceCallLanguage.English],
    description: 'Male American',
  },
  [ElevenLabsVoicePersona.Bill]: {
    persona: 'Bill',
    gender: Gender.Male,
    languages: [VoiceCallLanguage.English],
    description: 'Male American',
  },
  [ElevenLabsVoicePersona.Brian]: {
    persona: 'Brian',
    gender: Gender.Male,
    languages: [VoiceCallLanguage.English],
    description: 'Male American',
  },
  [ElevenLabsVoicePersona.Carolina]: {
    persona: 'Carolina',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.Multilingual, VoiceCallLanguage.Spanish],
    description: 'Female Spanish/Multilingual',
  },
  [ElevenLabsVoicePersona.Charlie]: {
    persona: 'Charlie',
    gender: Gender.Male,
    languages: [VoiceCallLanguage.English, VoiceCallLanguage.Multilingual],
    description: 'Male Australian/Multilingual',
  },
  [ElevenLabsVoicePersona.Dakota]: {
    persona: 'Dakota',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English],
    description: 'Female American',
  },
  [ElevenLabsVoicePersona.Daniel]: {
    persona: 'Daniel',
    gender: Gender.Male,
    languages: [VoiceCallLanguage.English],
    description: 'Male British',
  },
  [ElevenLabsVoicePersona.Dorothy]: {
    persona: 'Dorothy',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English],
    description: 'Female British',
  },
  [ElevenLabsVoicePersona.Eduardo]: {
    persona: 'Eduardo',
    gender: Gender.Male,
    languages: [VoiceCallLanguage.Spanish, VoiceCallLanguage.Multilingual],
    description: 'Male Spanish/Multilingual',
  },
  [ElevenLabsVoicePersona.Eric]: {
    persona: 'Eric',
    gender: Gender.Male,
    languages: [VoiceCallLanguage.English, VoiceCallLanguage.Multilingual],
    description: 'Male American/Multilingual',
  },
  [ElevenLabsVoicePersona.Gabriela]: {
    persona: 'Gabriela',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.Spanish],
    description: 'Female Spanish',
  },
  [ElevenLabsVoicePersona.Javier]: {
    persona: 'Javier',
    gender: Gender.Male,
    languages: [VoiceCallLanguage.Spanish],
    description: 'Male Spanish',
  },
  [ElevenLabsVoicePersona.Jessa]: {
    persona: 'Jessa',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English],
    description: 'Female American',
  },
  [ElevenLabsVoicePersona.Jessica]: {
    persona: 'Jessica',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English, VoiceCallLanguage.Multilingual],
    description: 'Female American/Multilingual',
  },
  [ElevenLabsVoicePersona.Josh]: {
    persona: 'Josh',
    gender: Gender.Male,
    languages: [VoiceCallLanguage.English],
    description: 'Male American',
  },
  [ElevenLabsVoicePersona.Kelli]: {
    persona: 'Kelli',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English],
    description: 'Female American Southern',
  },
  [ElevenLabsVoicePersona.Liam]: {
    persona: 'Liam',
    gender: Gender.Male,
    languages: [VoiceCallLanguage.English],
    description: 'Male American',
  },
  [ElevenLabsVoicePersona.Maribeth]: {
    persona: 'Maribeth',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English],
    description: 'Female American Southern',
  },
  [ElevenLabsVoicePersona.Matthew]: {
    persona: 'Matthew',
    gender: Gender.Male,
    languages: [VoiceCallLanguage.English],
    description: 'Male British',
  },
  [ElevenLabsVoicePersona.Nicole]: {
    persona: 'Nicole',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English],
    description: 'Female American',
  },
  [ElevenLabsVoicePersona.Paul]: {
    persona: 'Paul',
    gender: Gender.Male,
    languages: [VoiceCallLanguage.English],
    description: 'Male American',
  },
  [ElevenLabsVoicePersona.Rachel]: {
    persona: 'Rachel',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English],
    description: 'Female American',
  },
  [ElevenLabsVoicePersona.Rhett]: {
    persona: 'Rhett',
    gender: Gender.Male,
    languages: [VoiceCallLanguage.English],
    description: 'Male American Southern',
  },
  [ElevenLabsVoicePersona.Sarah]: {
    persona: 'Sarah',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English, VoiceCallLanguage.Multilingual],
    description: 'Female American/Multilingual',
  },
  [ElevenLabsVoicePersona.Serena]: {
    persona: 'Serena',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English],
    description: 'Female American',
  },
  [OpenAiStandardVoicePersona.Alloy]: {
    persona: 'Alloy',
    languages: [VoiceCallLanguage.English],
    description: 'Neutral and balanced',
  },
  [OpenAiStandardVoicePersona.Echo]: {
    persona: 'Echo',
    languages: [VoiceCallLanguage.English],
    description: 'Warm and engaging',
  },
  [OpenAiStandardVoicePersona.Shimmer]: {
    persona: 'Shimmer',
    languages: [VoiceCallLanguage.English],
    description: 'Energetic and expressive',
  },
  [OpenAiRealtimeVoicePersona.Marin]: {
    persona: 'Marin',
    languages: [VoiceCallLanguage.English],
    description: 'Professional and clear (Realtime only)',
  },
  [OpenAiRealtimeVoicePersona.Cedar]: {
    persona: 'Cedar',
    languages: [VoiceCallLanguage.English],
    description: 'Natural and conversational (Realtime only)',
  },
  [CartesiaVoicePersona.CustomerSupportLady]: {
    persona: 'Customer Support Lady',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English],
    description: 'Cartesia Sonic 3 - Customer Support Lady',
  },
  [CartesiaVoicePersona.Ariana]: {
    persona: 'Ariana',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English],
    description: 'Cartesia Sonic 3 - Ariana',
  },
  [CartesiaVoicePersona.MidWesternWomen]: {
    persona: 'Mid Western Women',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English],
    description: 'Cartesia Sonic 3 - Mid Western Women',
  },
  [CartesiaVoicePersona.Sarah]: {
    persona: 'Sarah',
    gender: Gender.Female,
    languages: [VoiceCallLanguage.English],
    description: 'Cartesia Sonic 3 - Sarah',
  },
};

export const VoiceCallFirstMessageModeDescriptions: Record<VoiceCallFirstMessageMode, string> = {
  [VoiceCallFirstMessageMode.AssistantSpeaksFirst]: 'Assistant speaks first',
  [VoiceCallFirstMessageMode.AssistantSpeaksFirstWithModelGeneratedMessage]:
    'Assistant speaks first with model generated message',
  [VoiceCallFirstMessageMode.AssistantWaitsForUser]: 'Assistant waits for user',
};
